import React, { useState, useMemo } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Chip,
    Stack,
    Paper
} from "@mui/material";
import {
    AutoFixHigh,
    Psychology,
    Description,
    Face,
    Shield,
    SettingsInputComponent,
    CheckCircle,
    Search
} from "@mui/icons-material";
import CustomButton from "../../components/common/CustomButton";
import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";

const FEATURES = [
    {
        id: "jd-gen",
        title: "Automated JD Generation",
        icon: <AutoFixHigh />,
        summary:
            "Create professional, industry-standard job descriptions instantly using AI.",
        steps: [
            "Go to JD Builder",
            "Enter role, experience, and tech stack",
            "Click Generate",
            "Review and publish"
        ]
    },
    {
        id: "resume-match",
        title: "Resume Matching",
        icon: <Description />,
        summary:
            "Match resumes with job descriptions using deep AI understanding.",
        steps: [
            "Select job description",
            "Apply using resumes",
            "View match score",
            "Analyze skill gaps"
        ]
    },
    {
        id: "adaptive-int",
        title: "LLM Adaptive Interviewing",
        icon: <Psychology />,
        summary:
            "Conduct smart interviews that adapt based on candidate responses.",
        steps: [
            "Enable Adaptive Mode",
            "Start interview session",
            "AI adjusts questions automatically",
            "Review insights after completion"
        ]
    },
    {
        id: "avatar",
        title: "AI Avatar Interviewer",
        icon: <Face />,
        summary:
            "Engage candidates with human-like AI interviewers.",
        steps: [
            "Select avatar",
            "Configure interview",
            "Start session",
            "View recordings"
        ]
    },
    {
        id: "proctoring",
        title: "Smart Proctoring",
        icon: <Shield />,
        summary:
            "Ensure fair interviews with advanced monitoring.",
        steps: [
            "Enable monitoring",
            "Track candidate behavior",
            "Detect anomalies",
            "Review reports"
        ]
    },
    // {
    //     id: "api",
    //     title: "API Integration",
    //     icon: <SettingsInputComponent />,
    //     summary:
    //         "Integrate seamlessly with your existing HR systems.",
    //     steps: [
    //         "Get API keys",
    //         "Connect your system",
    //         "Sync data",
    //         "Automate workflows"
    //     ]
    // }
];

export default function UserManual() {
    const [selectedId, setSelectedId] = useState(FEATURES[0].id);
    const [search, setSearch] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    const filteredFeatures = useMemo(() => {
        return FEATURES.filter((f) =>
            f.title.toLowerCase().includes(search.toLowerCase())
        );
    }, [search]);

    const activeFeature = useMemo(
        () => FEATURES.find((f) => f.id === selectedId),
        [selectedId]
    );

    return (
        <Box sx={{ bgcolor: "#F4F6F8", minHeight: "100vh", py: 4 }}>
            <Container maxWidth="xl">
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 4 }}
                >
                    {/* <CustomHeader title = "Product Guide" secondaryText= "Learn how to use each feature effectively"/> */}
                    <Box>
                        <Typography variant="h4" fontWeight="700">
                            Product Guide
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Learn how to use each feature effectively
                        </Typography>
                    </Box>
                </Stack>

                <Stack>
                    <Box>
                        <CustomInput
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            startIcon={<Search />}
                        />
                    </Box>
                </Stack>

                <Grid container spacing={3}>
                    {/* Sidebar */}
                    <Grid>
                        <Paper sx={{ p: 2, borderRadius: 1 }}>
                            <Stack spacing={1}>
                                {filteredFeatures.map((f) => (
                                    <CustomButton
                                        key={f.id}
                                        variant={selectedId === f.id ? "contained" : "text"}
                                        onClick={() => setSelectedId(f.id)}
                                        fullWidth
                                        sx={{ justifyContent: "flex-start", gap: 1 }}
                                    >
                                        {f.icon}
                                        <Typography variant="body2">{f.title}</Typography>
                                    </CustomButton>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Content */}
                    <Grid>
                        <CustomCard sx={{ p: 4, borderRadius: 3 }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h5" fontWeight="700">
                                        {activeFeature.title}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {activeFeature.summary}
                                    </Typography>
                                </Box>

                                {/* Visual Placeholder */}
                                <Box
                                    sx={{
                                        borderRadius: 1,
                                        bgcolor: "#EDEFF2",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        Feature Preview
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="subtitle1" fontWeight="600" mb={2}>
                                        Quick Steps
                                    </Typography>

                                    <List disablePadding>
                                        {activeFeature.steps.map((step, i) => (
                                            <ListItem key={i} disableGutters>
                                                <ListItemIcon sx={{ minWidth: 30 }}>
                                                    <CheckCircle fontSize="small" color="primary" />
                                                </ListItemIcon>
                                                <ListItemText primary={step} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Stack direction="row" spacing={2}>
                                    <CustomButton
                                        variant="contained"
                                        onClick={() =>
                                            setSnackbar({
                                                open: true,
                                                message: "Link copied"
                                            })
                                        }
                                    >
                                        Share Guide
                                    </CustomButton>
                                </Stack>
                            </Stack>
                        </CustomCard>
                    </Grid>
                </Grid>
            </Container>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Box>
    );
}